import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Button } from '@mui/material';
import { Door } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { type FC, useLayoutEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import SearchableCheckboxList from 'v2/components/SearchableCheckboxList/SearchableCheckboxList';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import useWizardStepContext from 'v2/components/WizardModal/useWizardStepContext';

const FORM_ID = 'device-group-devices-step-form';
const STEP_INDEX = 2;

interface FormValues {
  doorIds: string[];
}

interface Props {
  groupId: string | null;
  groupName: string | null;
  allDoors: Door[];
  updateDeviceGroupWithDoors: (
    groupId: string,
    groupName: string,
    doorIds: string[]
  ) => Promise<{ success: boolean; errorMessage?: string }>;
}

const DeviceGroupDevicesStep: FC<Props> = ({ groupId, groupName, allDoors, updateDeviceGroupWithDoors }) => {
  const { translation } = useTranslation();
  const { showSnackbar } = useSnackbarContext();
  const { goToPrev, goToNext, setFooterContent, steps } = useWizardStepContext();
  const [isLoading, setIsLoading] = useState(false);

  const { handleSubmit, control } = useForm<FormValues>({ mode: 'onTouched', defaultValues: { doorIds: [] } });

  const onSubmit = handleSubmit(async (data) => {
    if (!groupId || !groupName) return;
    setIsLoading(true);
    try {
      if (data.doorIds.length > 0) {
        const result = await updateDeviceGroupWithDoors(groupId, groupName, data.doorIds);
        if (!result.success) {
          showSnackbar(result.errorMessage ?? translation.an_error_occurred, { severity: 'error' });
          return;
        }
      }
      goToNext();
    } finally {
      setIsLoading(false);
    }
  });

  useLayoutEffect(() => {
    setFooterContent(
      STEP_INDEX,
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', md: 'row' }, gap: 2 }}>
        <Button
          key="device-group-devices-step-back"
          id="device-group-devices-back-button"
          variant="outlined"
          type="button"
          onClick={goToPrev}
        >
          {translation.back}
        </Button>
        <Button
          key="device-group-devices-step-submit"
          id="device-group-devices-finish-button"
          variant="contained"
          type="submit"
          form={FORM_ID}
          loading={isLoading}
          endIcon={<ChevronRightIcon />}
        >
          {translation.finish}
        </Button>
      </Box>
    );
  }, [goToPrev, isLoading, setFooterContent, steps, translation]);

  return (
    <form id={FORM_ID} onSubmit={onSubmit} noValidate>
      <Controller
        name="doorIds"
        control={control}
        render={({ field }) => (
          <SearchableCheckboxList
            items={allDoors}
            selectedIds={field.value}
            onChange={field.onChange}
            idPrefix="devices-"
          />
        )}
      />
    </form>
  );
};

export default DeviceGroupDevicesStep;
